/**
 * ProductSchema — inject SoftwareApplication + Product JSON-LD into
 * the document <head> so Google, Bing, and LLM crawlers (ChatGPT,
 * Claude, Gemini) can pick up structured pricing, features, and
 * aggregate ratings.
 *
 * Drop this component into any high-traffic public route (Home,
 * About, city landing pages) — it renders no visible DOM.
 *
 * Fields exposed:
 *   • name, description, applicationCategory
 *   • offers.price (subscription monthly)
 *   • aggregateRating (ratingValue + reviewCount)
 *   • image + videoObject (for rich-snippet visuals)
 *   • featureList (bulleted feature array)
 *   • areaServed (Canada + provinces)
 */
import { useEffect } from "react";

const PRODUCT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Central Rentals Canada",
  "applicationCategory": "BusinessApplication",
  "applicationSubCategory": "Property Management Software",
  "operatingSystem": "Web, iOS, Android",
  "url": "https://www.centralrentals.ca",
  "description":
    "The premier digital landlord kit for apartment communities — connecting Canadian landlords, property-management companies, and multifamily operators. Subscription-only, 0% rent cut, works in every Canadian province.",
  "brand": {
    "@type": "Brand",
    "name": "Central Rentals Canada",
    "logo": "https://www.centralrentals.ca/logo.png",
  },
  "image": [
    "https://www.centralrentals.ca/og-hero.jpg",
    "https://www.centralrentals.ca/screenshots/dashboard.jpg",
    "https://www.centralrentals.ca/screenshots/copilot.jpg",
  ],
  "video": {
    "@type": "VideoObject",
    "name": "Central Rentals Canada — 60-second product tour",
    "description": "See how landlords in Ontario, BC, and Quebec run their portfolios end-to-end on Central Rentals Canada.",
    "thumbnailUrl": "https://www.centralrentals.ca/og-video-thumb.jpg",
    "uploadDate": "2026-02-01",
    "contentUrl": "https://www.centralrentals.ca/product-tour.mp4",
  },
  "featureList": [
    "AI-powered PM Copilot with unified inbox + priority queue",
    "Sublease & lease-assignment marketplace (RTA §97 compliant)",
    "Compliance Engine for Ontario / BC / Quebec regulatory changes",
    "Rent collection, e-signable leases, tenant screening",
    "Multi-entity trust accounting with owner-draw workflows",
    "Preventive maintenance scheduler + warranty tracking",
    "AI transaction categorizer with YTD NOI reporting",
    "Portfolio migration from AppFolio, Buildium, Yardi, Rentec, Rent Manager, and any CSV",
    "Autonomous voice orchestration (Twilio outbound multi-agent)",
    "Computer-vision walkthrough + roofing inspection reports",
    "Traceable lease abstraction with source-page citations",
    "Auto-writing landlord emails and pre-built response templates",
    "Tenant self-serve help desk with auto ticket escalation",
    "0% platform rent cut, Stripe + Klarna Pay-in-4 supported",
  ],
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "CAD",
    "lowPrice": "0",
    "highPrice": "59",
    "offerCount": 3,
    "availability": "https://schema.org/InStock",
    "offers": [
      {
        "@type": "Offer",
        "name": "Free browsing",
        "price": "0",
        "priceCurrency": "CAD",
        "description": "Browse resolution Q&As, tenant helpdesk topics, and compliance updates.",
      },
      {
        "@type": "Offer",
        "name": "Landlord subscription",
        "price": "59",
        "priceCurrency": "CAD",
        "billingIncrement": "P1M",
        "description": "Full landlord kit — 1 to 100 units, all AI features included.",
      },
      {
        "@type": "Offer",
        "name": "Brokerage subscription",
        "price": "199",
        "priceCurrency": "CAD",
        "billingIncrement": "P1M",
        "description": "PM workspace with unlimited managed landlords + sign-in-as landlord.",
      },
    ],
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "312",
    "bestRating": "5",
    "worstRating": "1",
  },
  "areaServed": [
    {"@type": "Country", "name": "Canada"},
    {"@type": "AdministrativeArea", "name": "Ontario"},
    {"@type": "AdministrativeArea", "name": "British Columbia"},
    {"@type": "AdministrativeArea", "name": "Alberta"},
    {"@type": "AdministrativeArea", "name": "Quebec"},
  ],
  "audience": {
    "@type": "Audience",
    "audienceType": "Canadian landlords, property managers, and multifamily operators",
  },
  "provider": {
    "@type": "Organization",
    "name": "Central Rentals Canada",
    "url": "https://www.centralrentals.ca",
    "foundingDate": "2009",
    "sameAs": [
      "https://www.linkedin.com/company/central-rentals-canada",
    ],
  },
};

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How much can I raise the rent in Ontario in 2026?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The 2026 rent-increase guideline for Ontario rent-controlled units is published by the LTB. You must give the tenant at least 90 days' written notice using Form N1 and cannot raise rent more than once every 12 months.",
      },
    },
    {
      "@type": "Question",
      "name": "Does Central Rentals Canada take a cut of my rent?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Central Rentals Canada is a subscription-only platform. 0% of the rent your tenants pay ever touches our margin — you keep every dollar.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I migrate from AppFolio, Buildium, Yardi, or Rentec?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Central Rentals Canada has a one-click portfolio migration importer that supports AppFolio, Buildium, Yardi Voyager / Breeze, Rentec Direct, Rent Manager, and universal CSV exports. Preview mapping, dry-run import, and one-click undo included.",
      },
    },
    {
      "@type": "Question",
      "name": "Which Canadian provinces does Central Rentals Canada support?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "All 10 provinces. Provincial rule content, tribunal forms (LTB, RTB, TAL), and rent-guideline data are curated for Ontario, BC, Alberta, Quebec, and Nova Scotia in detail, with baseline support for the remaining provinces.",
      },
    },
  ],
};

export default function ProductSchema({ withFaq = true }) {
  useEffect(() => {
    const inject = (id, obj) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.id = id;
        el.type = "application/ld+json";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(obj);
    };
    inject("cr-product-jsonld", PRODUCT_JSONLD);
    if (withFaq) inject("cr-faq-jsonld", FAQ_JSONLD);
    return () => {
      // Leave the tags on route change so LLM crawlers keep finding them.
    };
  }, [withFaq]);
  return null;
}
